// Адрес сервера: где программа ищет данные.
//
// ## Зачем это отдельным механизмом
//
// Установщик для Windows собирается один раз, а серверов у него будет несколько: демонстрация,
// опытная эксплуатация, боевой контур заказчика. Адрес, зашитый в сборку, означает, что при
// переезде сервера всем придётся ставить новую программу; а собранная с адресом разработчика
// программа у заказчика просто не найдёт сервер и скажет «нет связи» — без единой подсказки,
// что именно настроить.
//
// Поэтому порядок такой: сохранённый человеком адрес → адрес из сборки → локальный сервер
// разработки. Первое сильнее второго намеренно: человек, который однажды указал адрес, не
// должен указывать его снова после обновления программы.
//
// Хранится в localStorage, а не в файле настроек Tauri: тот же механизм работает и в браузере,
// а браузерная форма у нас служебная, но существует.

const КЛЮЧ = 'bark.server-address';

/** Откат на локальный сервер разработки. Боевой адрес приходит переменной сборки или настройкой. */
const ЛОКАЛЬНЫЙ = 'http://127.0.0.1:55800';

function изСборки(): string | null {
  const значение = import.meta.env.VITE_API_BASE_URL;
  return typeof значение === 'string' && значение !== '' ? значение : null;
}

/**
 * «Тот же адрес, что и страница»: пустая строка, из которой клиент собирает относительный путь.
 *
 * Боевой веб раздаётся одним входом со статикой и `/api`, и адрес сервера ему не нужен вовсе.
 * До 26.08.2026 этой ветки не было: прод-сборка без адреса сборки проваливалась в локальный
 * откат, браузер бился в 127.0.0.1 чужой машины, и первый живой вход на боевом контуре
 * встретил «нет связи с сервером» при живом сервере в том же адресе.
 *
 * Ветка не срабатывает в разработке (там страница живёт на порту сборщика, а сервер на своём)
 * и в десктопной обёртке (страница приезжает из самой программы, не по http, — адрес задаёт
 * человек на входе).
 */
function тотЖеАдрес(): string | null {
  if (import.meta.env.DEV) {
    return null;
  }
  const протокол = window.location.protocol;
  return протокол === 'http:' || протокол === 'https:' ? '' : null;
}

function сохранённый(): string | null {
  try {
    const значение = localStorage.getItem(КЛЮЧ);
    return значение !== null && значение !== '' ? значение : null;
  } catch {
    // Хранилище недоступно (частный режим, запрет в обёртке) — работаем на адресе сборки.
    return null;
  }
}

/** Адрес, по которому программа ходит за данными прямо сейчас. Пустая строка — тот же адрес. */
export function serverAddress(): string {
  return сохранённый() ?? изСборки() ?? тотЖеАдрес() ?? ЛОКАЛЬНЫЙ;
}

/** Настроен ли адрес. Ложь — программа смотрит в локальный сервер разработки. */
export function serverAddressConfigured(): boolean {
  return сохранённый() !== null || изСборки() !== null || тотЖеАдрес() !== null;
}

/** Задан ли адрес именно человеком (а не сборкой). Нужно экрану настроек. */
export function serverAddressIsCustom(): boolean {
  return сохранённый() !== null;
}

/**
 * Привести введённое к рабочему виду: убрать хвостовой слэш, дописать протокол.
 *
 * Человек напечатает «bark.uxcode.agency» или «192.168.1.10:8000» — и то и другое законно.
 * Без протокола `fetch` считает такой адрес относительным путём и молча стучится сам в себя.
 */
export function normalizeServerAddress(сырой: string): string {
  const очищенный = сырой.trim().replace(/\/+$/, '');
  if (очищенный === '') {
    return '';
  }
  if (/^https?:\/\//i.test(очищенный)) {
    return очищенный;
  }
  // Локальные адреса и адреса с портом чаще всего без шифрования, остальные — с ним.
  const локальный = /^(localhost|127\.0\.0\.1|\d+\.\d+\.\d+\.\d+)(:\d+)?$/i.test(очищенный);
  return `${локальный ? 'http' : 'https'}://${очищенный}`;
}

/**
 * Годится ли адрес планшету.
 *
 * На планшете обращение по открытому протоколу (`http://`) система блокирует сама, и человек
 * видит «нет связи» при живом сервере — без единой подсказки, что дело в протоколе. Поэтому
 * на планшете такой адрес не сохраняется вовсе, а отказ объясняется словами. Ровно из-за этого
 * поле адреса там сначала спрятали целиком; прятать его нельзя: сборка в магазине ОДНА, а
 * серверов два — рабочий заказчика и показательный для проверяющего из магазина.
 */
export function адресПодходитПланшету(сырой: string): boolean {
  const приведённый = normalizeServerAddress(сырой);
  return приведённый === '' || приведённый.toLowerCase().startsWith('https://');
}

export function setServerAddress(адрес: string): void {
  const приведённый = normalizeServerAddress(адрес);
  try {
    if (приведённый === '') {
      localStorage.removeItem(КЛЮЧ);
    } else {
      localStorage.setItem(КЛЮЧ, приведённый);
    }
  } catch {
    // Не сохранилось — адрес будет действовать до перезапуска. Это лучше, чем отказ работать.
  }
}

/**
 * Проверить, что по адресу действительно отвечает сервер Барка.
 *
 * Проверяется ДО сохранения: человек, сохранивший опечатку, увидит «нет связи» на экране входа
 * и не поймёт, что дело в адресе. Здесь же он видит ответ сразу.
 */
export async function checkServerAddress(адрес: string): Promise<{ ok: boolean; reason?: string }> {
  const приведённый = normalizeServerAddress(адрес);
  if (приведённый === '') {
    return { ok: false, reason: 'адрес пустой' };
  }
  try {
    // Свой срок ожидания обязателен: у недоступного адреса браузер ждёт своё умолчание —
    // минуту и дольше, — и всё это время человек смотрит на «Проверяем связь…» без всякого
    // ответа. Пять секунд достаточно даже для медленного канала.
    const ответ = await fetch(`${приведённый}/api/v1/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    if (!ответ.ok) {
      return { ok: false, reason: `сервер ответил ${ответ.status}` };
    }
    const состав: unknown = await ответ.json().catch(() => null);
    const это_барк =
      состав !== null && typeof состав === 'object' && 'status' in (состав as object);
    return это_барк ? { ok: true } : { ok: false, reason: 'по адресу отвечает не сервер Барка' };
  } catch (ошибка) {
    const истекло = ошибка instanceof DOMException && ошибка.name === 'TimeoutError';
    return {
      ok: false,
      reason: истекло
        ? 'сервер не ответил за пять секунд: проверьте адрес и сеть'
        : 'по этому адресу никто не отвечает',
    };
  }
}
